import React from "react";

/**
 * ProductInfoModal — luxury-aesthetic product information popup rendered
 * before the client confirms adding an option to their cart.
 *
 * Reads from `product_info` on the treatment record. Falls back to an
 * "Information being updated" notice when `product_info.verified` is
 * false (so unverified ingredients are NEVER presented as confirmed
 * facts to the client).
 *
 * Two primary actions:
 *   - "Back to Treatments"       → closes without touching the cart
 *   - "Continue to Add to Cart"  → calls the existing add-to-cart flow
 *                                  then closes the modal
 *
 * Deduplication: `isAdding` guards against double-tap on the primary
 * CTA so a flaky connection doesn't accidentally add the same item
 * twice when the cart doesn't support quantity.
 */

const Section = ({ title, children, testid }) => (
  <section className="mt-6 first:mt-0" data-testid={testid}>
    <h4 className="mono-label text-[10px] tracking-[0.24em] text-stone-500">{title}</h4>
    <div className="mt-2 text-sm text-stone-800 leading-relaxed whitespace-pre-line">
      {children}
    </div>
  </section>
);

export default function ProductInfoModal({ treatment, onClose, onConfirm }) {
  const [isAdding, setIsAdding] = React.useState(false);
  const info = (treatment && treatment.product_info) || {};
  const verified = !!info.verified;

  const handleConfirm = async () => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      await onConfirm(treatment);
    } finally {
      // Close after confirm — the parent will toast & close anyway, but we
      // keep the guard latched for the lifetime of this modal instance.
    }
  };

  // Lock body scroll while open — same pattern as TreatmentDetailModal
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!treatment) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-stone-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="product-info-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[94vh] overflow-y-auto border hairline shadow-2xl"
        style={{ background: "linear-gradient(180deg, #fdfbf7 0%, #ffffff 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b hairline px-6 sm:px-8 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="mono-label text-[10px] tracking-[0.24em] text-stone-500">PRODUCT INFORMATION</div>
            <h3 className="mt-1 font-serif text-2xl sm:text-3xl text-stone-900 leading-tight" data-testid="product-info-name">
              {treatment.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="product-info-close"
            aria-label="Close product information"
            className="shrink-0 w-9 h-9 border hairline flex items-center justify-center text-stone-500 hover:text-stone-900 hover:border-stone-900 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1 L13 13 M13 1 L1 13" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 py-6">
          {!verified ? (
            <div
              className="border hairline bg-amber-50 border-amber-300 px-4 py-6 text-center"
              data-testid="product-info-unverified"
            >
              <p className="mono-label text-[11px] tracking-[0.22em] text-amber-900">
                INFORMATION BEING UPDATED
              </p>
              <p className="mt-2 text-sm text-stone-700">
                We're verifying this product's ingredient list and precautions. Please speak to
                your practitioner for the full product details before your appointment.
              </p>
            </div>
          ) : (
            <>
              {info.overview && (
                <Section title="PRODUCT OVERVIEW" testid="product-info-overview">{info.overview}</Section>
              )}
              {info.key_ingredients && (
                <Section title="KEY INGREDIENTS" testid="product-info-ingredients">{info.key_ingredients}</Section>
              )}
              {info.benefits && (
                <Section title="EXPECTED BENEFITS" testid="product-info-benefits">{info.benefits}</Section>
              )}
              {info.treatment_method && (
                <Section title="TREATMENT METHOD" testid="product-info-method">{info.treatment_method}</Section>
              )}
              {info.precautions && (
                <Section title="RELEVANT PRECAUTIONS" testid="product-info-precautions">{info.precautions}</Section>
              )}
              {info.disclaimer && (
                <Section title="IMPORTANT PRODUCT INFORMATION" testid="product-info-disclaimer">{info.disclaimer}</Section>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t hairline px-6 sm:px-8 py-4 flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="product-info-back"
            className="mono-label text-[11px] tracking-[0.22em] text-stone-500 hover:text-stone-900 border hairline py-3 px-6 transition-colors"
          >
            BACK TO TREATMENTS
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isAdding}
            data-testid="product-info-confirm"
            className="mono-label text-[11px] tracking-[0.22em] bg-stone-900 text-white py-3 px-6 hover:bg-stone-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ color: "#fff" }}
          >
            {isAdding ? "ADDING…" : "CONTINUE TO ADD TO CART →"}
          </button>
        </div>
      </div>
    </div>
  );
}
